import { getApiErrorMessage } from '@/utils/apiError'
import { apiClient } from './api'

export interface ConfigurationResponse<T> {
  success: boolean
  data?: T
  error?: string
}

// Configuration type names
export type ConfigurationType =
  | 'business_basics'
  | 'hours_coverage'
  | 'store_context'
  | 'engagement_channels'
  | 'conversation_style'
  | 'conversation_flow'
  | 'reminders_follow_up'
  | 'inventory_pricing'
  | 'compliance_risk'
  | 'appointment_recovery'
  | 'showroom_unsold_recovery'
  | 'appointment_reminders'
  | 'prequalification'

/**
 * Get configuration for a specific dealership
 */
export async function getConfiguration<T>(
  dealershipId: string,
  configType: ConfigurationType,
): Promise<ConfigurationResponse<T>> {
  try {
    const response = await apiClient.get<ConfigurationResponse<T>>(`/dealerships/${dealershipId}/configurations/${configType}`)
    return response
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to load configuration',
    }
  }
}

/**
 * Save configuration for a specific dealership
 */
export async function saveConfiguration<T>(
  dealershipId: string,
  configType: ConfigurationType,
  data: T,
): Promise<ConfigurationResponse<T>> {
  try {
    const response = await apiClient.put<ConfigurationResponse<T>>(`/dealerships/${dealershipId}/configurations/${configType}`, {
      data,
    })
    return response
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to save configuration',
    }
  }
}

/**
 * Get configuration for a dealership group
 */
export async function getGroupConfiguration<T>(groupId: string): Promise<ConfigurationResponse<T>> {
  try {
    const response = await apiClient.get<{ success: boolean; data: any }>(`/dealership-groups/${groupId}`)
    return {
      success: response.success,
      data: response.data?.configurationData as T,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to load group configuration',
    }
  }
}

/**
 * Save configuration for a dealership group
 */
export async function updateGroupConfiguration<T>(groupId: string, data: T): Promise<ConfigurationResponse<T>> {
  try {
    const response = await apiClient.put<{ success: boolean; data: any }>(`/dealership-groups/${groupId}`, {
      configurationData: data,
    })
    return {
      success: response.success,
      data: response.data?.configurationData as T,
    }
  } catch (error: any) {
    return {
      success: false,
      error: getApiErrorMessage(error, 'Failed to save group configuration'),
    }
  }
}
